import { DownOutlined, FolderOutlined, RightOutlined } from "@ant-design/icons";
import clsx from "clsx";
import { useCallback, useState, type MouseEvent } from "react";
import BEmpty from "../BEmpty/BEmpty";
import "./BProjectList.scss";

export type BProjectListItem = {
  id: string;
  title: string;
};

export type BProjectListProps = {
  items: BProjectListItem[];
  activeId?: string | null;
  onSelect?: (id: string) => void;
  title?: string;
  collapsed?: boolean;
  defaultCollapsed?: boolean;
  onCollapsedChange?: (collapsed: boolean) => void;
  emptyDescription?: string;
  className?: string;
};

const BProjectList = ({
  items,
  activeId,
  onSelect,
  title = "Dự án",
  collapsed,
  defaultCollapsed = false,
  onCollapsedChange,
  emptyDescription = "Chưa có dự án",
  className,
}: BProjectListProps) => {
  const isCollapsedControlled = collapsed !== undefined;
  const [internalCollapsed, setInternalCollapsed] = useState(defaultCollapsed);
  const isCollapsed = isCollapsedControlled ? collapsed : internalCollapsed;

  const handleToggleCollapse = useCallback(() => {
    const next = !isCollapsed;
    if (!isCollapsedControlled) {
      setInternalCollapsed(next);
    }
    onCollapsedChange?.(next);
  }, [isCollapsed, isCollapsedControlled, onCollapsedChange]);

  const handleSelect = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      const id = event.currentTarget.dataset.id;
      if (id) {
        onSelect?.(id);
      }
    },
    [onSelect],
  );

  return (
    <div className={clsx("b-project-list", className)}>
      <div className="b-project-list__header">
        <span className="b-project-list__title">{title}</span>
        <button
          type="button"
          className="b-project-list__collapse"
          aria-label={isCollapsed ? "Mở rộng dự án" : "Thu gọn dự án"}
          aria-expanded={!isCollapsed}
          onClick={handleToggleCollapse}
        >
          {isCollapsed ? <RightOutlined /> : <DownOutlined />}
        </button>
      </div>

      {!isCollapsed ? (
        items.length === 0 ? (
          <BEmpty description={emptyDescription} />
        ) : (
          <ul className="b-project-list__list">
            {items.map((item) => {
              const isActive = activeId === item.id;

              return (
                <li key={item.id} className="b-project-list__row">
                  <button
                    type="button"
                    className={clsx("b-project-list__item", {
                      "b-project-list__item--active": isActive,
                    })}
                    data-id={item.id}
                    data-active={isActive || undefined}
                    onClick={handleSelect}
                  >
                    <FolderOutlined className="b-project-list__icon" />
                    <span className="b-project-list__name">{item.title}</span>
                  </button>
                </li>
              );
            })}
          </ul>
        )
      ) : null}
    </div>
  );
};

export default BProjectList;
